import { Link, useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/common/BrandLogo';
import useContactSupport from '@/hooks/useContactSupport';
import { useGetContactSupportQuery } from '@/store/api/userApi';
import { Icons } from '@/utils/Icons';

/** 404 for unknown URLs, in the site theme. Rendered outside the layouts (no header/sidebar). */
export function NotFoundPage() {
  const navigate = useNavigate();
  // No Header here to load the support links, so fetch them for the help buttons.
  useGetContactSupportQuery(undefined);
  const { handleWhatsAppSupportClick, handleTelegramSupportClick } = useContactSupport();

  return (
    <div className="flex min-h-screen flex-col bg-shell bg-auth-v2 px-4 py-6 text-shell-fg">
      <Link to="/home" className="self-center sm:self-start">
        <BrandLogo className="h-10 w-auto object-contain sm:h-12" />
      </Link>

      <main className="flex flex-1 items-center justify-center py-10">
        <div className="w-full max-w-xl space-y-8 text-center">
          <div className="relative">
            <p
              aria-hidden="true"
              className="select-none text-[8rem] font-black leading-none text-gold/15 sm:text-[11rem]"
            >
              404
            </p>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex size-24 items-center justify-center rounded-full border border-gold/40 bg-shell-panel text-gold shadow-2xl sm:size-28">
                <Icons.Search className="size-10 sm:size-12" />
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl font-bold sm:text-4xl">Page Not Found</h1>
            <p className="mx-auto max-w-md text-sm text-shell-muted sm:text-base">
              Sorry, we couldn&apos;t find the page you&apos;re looking for. It may have been moved,
              or the link may be wrong.
            </p>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-shell-border bg-shell-panel px-6 text-sm font-semibold transition hover:border-gold sm:w-auto"
            >
              <Icons.previous className="size-3.5" aria-hidden="true" />
              Go Back
            </button>
            <Link
              to="/home"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-gold px-6 text-sm font-semibold text-gold-foreground transition hover:bg-gold-hover sm:w-auto"
            >
              <Icons.Home className="size-4" aria-hidden="true" />
              Home
            </Link>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 border-t border-shell-border pt-6 text-sm text-shell-muted sm:flex-row">
            <span>Need help? Contact support:</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleWhatsAppSupportClick}
                className="flex h-9 items-center gap-2 rounded-full border border-shell-border bg-shell-panel px-4 font-medium text-shell-fg transition hover:border-gold"
              >
                <Icons.whatsapp className="size-4 text-success-emerald" aria-hidden="true" />
                WhatsApp
              </button>
              <button
                type="button"
                onClick={handleTelegramSupportClick}
                className="flex h-9 items-center gap-2 rounded-full border border-shell-border bg-shell-panel px-4 font-medium text-shell-fg transition hover:border-gold"
              >
                <Icons.telegram className="size-4 text-info-cyan" aria-hidden="true" />
                Telegram
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
